import React, { useCallback, useMemo } from 'react';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import cn from 'classnames';
import { DateTime } from 'luxon';
import { MdError, MdOutlineRemoveCircle, MdPersonAdd } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { RenewalStatus } from '@appTypes/enums';
import {
  OrgPositionDetail,
  PersonAssigned,
} from '@appTypes/esb/organizations/positions/_orgGuid';
import { organizationGuidSel } from '@context';
import {
  PositionsAllowedToHaveCR,
  PositionsId, //TODO: Uncomment for SBL-5789
  // PositionsProhibitedSubUnitSupport,
  // PositionsProhibitedUnitSupport,
  // SubUnitSupportPositionIds,
  // UnitSupportPositionIds,
} from '@modules/permissionManager/constants';
import {
  PersonAssignedWithRegistration,
  RosterAdultMemberWitRegistration,
} from '@modules/permissionManager/types';
import {
  usePostAssignFunctionalPositionsMutation,
  usePostExpireFunctionalPositionsMutation,
  usePostReassignPositionsMutation,
} from '@modules/rtk/esb-api';
import { intl } from '@modules/shared';
import { toastService } from '@modules/toasts';
import { Button, Card, PersonAvatar, S, Select } from '@shared/components';

import styles from '../assignees.less';

interface PositionSelectorProps {
  positionData: OrgPositionDetail;
  unitUsers?: RosterAdultMemberWitRegistration[];
  loading?: boolean;
  positionsWithMoreUsersThanMax?: OrgPositionDetail[];
  registeredPositions?: OrgPositionDetail[];
  functionalPositions?: OrgPositionDetail[];
}

export const PositionSelector = ({
  positionData,
  unitUsers = [],
  loading = false,
  positionsWithMoreUsersThanMax = [],
  registeredPositions = [],
}: // functionalPositions = [], //TODO: uncomment for SBL-5789
PositionSelectorProps) => {
  const [assignFunctionalPosition, { isLoading: isSavingFunctionalPositon }] =
    usePostAssignFunctionalPositionsMutation();
  const [expireFunctionalPosition, { isLoading: isExpiringFunctionalPositon }] =
    usePostExpireFunctionalPositionsMutation();
  const [reAssignPositions, { isLoading: isReassigningPositon }] =
    usePostReassignPositionsMutation();
  const orgGuid = useSelector(organizationGuidSel);
  const {
    PositionId,
    positionLong,
    max,
    min,
    personsAssigned = [],
    isYouthPosition,
    isFunctional,
  } = positionData;

  const getCanAssignPosition = useCallback(
    (unitUser: RosterAdultMemberWitRegistration) => {
      const isAlreadyAssigned = !!personsAssigned.find(
        person => person.personGuid === unitUser.personGuid,
      );
      if (isAlreadyAssigned) return false;

      // This is for registered positions only
      if (!isFunctional && registeredPositions.length) {
        const userRegisteredPosiotions = registeredPositions.filter(regPos =>
          regPos.personsAssigned.find(
            person => person.personGuid === unitUser.personGuid,
          ),
        );

        // User doesn't have registered positions
        if (!userRegisteredPosiotions.length) return true;

        // Users should only have 1 registered position, they could have 2 if the positions are a certain pairs, but no more.
        if (userRegisteredPosiotions.length >= 2) return false;

        // At this point we know for sure the user has only 1 registered position.
        const isUserPositionCR =
          userRegisteredPosiotions[0].PositionId === PositionsId.CR;
        const isUserPositionAllowedToHaveCR = PositionsAllowedToHaveCR.includes(
          userRegisteredPosiotions[0].PositionId,
        );

        // Valid pair combinations of positions:
        // CR + CC
        // CR + MC
        // CR + NM
        // CR + PT
        if (isUserPositionCR) {
          return PositionsAllowedToHaveCR.includes(PositionId);
        }
        if (isUserPositionAllowedToHaveCR) {
          return PositionId === PositionsId.CR;
        }

        return false;
      }

      // TODO: Uncomment when SBL-5789 has the missing positions
      // Only for unit support positions
      // if (PositionsId.US === PositionId) {
      //   const allPositions = registeredPositions.concat(functionalPositions);
      //   const prohibitedPositions = allPositions.filter(pos =>
      //     PositionsProhibitedUnitSupport.includes(pos.PositionId),
      //   );
      //   const hasUserProhibitedPosition = prohibitedPositions.some(pos =>
      //     pos.personsAssigned.find(
      //       person => person.personGuid === unitUser.personGuid,
      //     ),
      //   );
      //   return !hasUserProhibitedPosition;
      // }

      // Only for sub-unit support positions
      // if (PositionsId.SUS === PositionId) {
      //   const allPositions = registeredPositions.concat(functionalPositions);
      //   const prohibitedPositions = allPositions.filter(pos =>
      //     PositionsProhibitedSubUnitSupport.includes(pos.PositionId),
      //   );
      //   const hasUserProhibitedPosition = prohibitedPositions.some(pos =>
      //     pos.personsAssigned.find(
      //       person => person.personGuid === unitUser.personGuid,
      //     ),
      //   );
      //   return !hasUserProhibitedPosition;
      // }

      return true;
    },
    [isFunctional, personsAssigned, registeredPositions, PositionId],
  );

  const getRegistration = useCallback(
    (validPerson: PersonAssigned) => {
      const foundUser = unitUsers.find(
        user => validPerson.personGuid === user.personGuid,
      );

      if (foundUser) {
        return foundUser;
      }

      return {
        hasValidRegistration: false,
        renewalStatus: RenewalStatus.NeedsToRegister,
        registrationExpireDt: '',
      };
    },
    [unitUsers],
  );

  const { lapsedList, activeList } = useMemo(() => {
    const lapsedList: PersonAssignedWithRegistration[] = [];
    const activeList: PersonAssignedWithRegistration[] = [];

    personsAssigned.forEach(person => {
      const { hasValidRegistration, renewalStatus, registrationExpireDt } =
        getRegistration(person);
      if (hasValidRegistration) {
        activeList.push({
          ...person,
          hasValidRegistration,
          renewalStatus,
          registrationExpireDt,
        });
        return;
      }
      lapsedList.push({
        ...person,
        hasValidRegistration,
        renewalStatus,
        registrationExpireDt,
      });
    });

    return { lapsedList, activeList };
  }, [personsAssigned, getRegistration]);

  const isSaving =
    isSavingFunctionalPositon ||
    isExpiringFunctionalPositon ||
    isReassigningPositon;
  const hasNoMax = max > 99;
  const showRequiredMsg = min > 0 && !activeList.length;

  const handleAddFunctionalPosition = async (personGuid: string) => {
    try {
      await assignFunctionalPosition({
        personGuid,
        body: {
          PositionId,
          OrganizationGuid: orgGuid,
          EffectiveDt: DateTime.now().toISODate(),
        },
      }).unwrap();
      toastService.success(
        <FormattedMessage id="permissionManager.assignees.successMessage" />,
      );
    } catch (error) {
      const err = error as FetchBaseQueryError;
      if (err.status === 403) return;
      if (err.status === 400) {
        toastService.error(
          intl.formatMessage({ id: 'shared.apiUnauthorized' }),
        );
        return;
      }

      toastService.error(error.data.errorDesc);
    }
  };

  const handleExpireFunctionalPosition = async (
    personAssigned: PersonAssigned,
  ) => {
    try {
      await expireFunctionalPosition({
        personGuid: personAssigned.personGuid,
        body: {
          PositionId,
          PkPersonPositionId: personAssigned.pkpersonPositionId,
          OrganizationGuid: orgGuid,
          ExpiryDt: DateTime.now().toISODate(),
        },
      }).unwrap();
      toastService.success(
        <FormattedMessage id="permissionManager.assignees.successMessage" />,
      );
    } catch (error) {
      toastService.error(error.data.errorDesc);
    }
  };

  const handleReasignRegisteredPosition = async (
    addPersonGuid?: string,
    expirePersonGuid?: string,
  ) => {
    const assign = addPersonGuid
      ? [
          {
            personguid: addPersonGuid,
            EffectiveDt: DateTime.now().toISODate(),
          },
        ]
      : [];

    const expite = expirePersonGuid ? [expirePersonGuid] : [];

    try {
      await reAssignPositions({
        orgGuid,
        body: [
          {
            PositionId: `${PositionId}`,
            Assign: assign,
            Expire: expite,
          },
        ],
      }).unwrap();
      toastService.success(
        <FormattedMessage id="permissionManager.assignees.successMessage" />,
      );
    } catch (error) {
      const err = error as FetchBaseQueryError;
      if (err.status === 403) return;
      toastService.error(error.data.errorDesc);
    }
  };

  const handleReasignFunctionalPosition = async (
    addPersonGuid: string,
    currentPersonAssigned: PersonAssigned,
  ) => {
    try {
      await handleExpireFunctionalPosition(currentPersonAssigned);
      await handleAddFunctionalPosition(addPersonGuid);
      toastService.success(
        <FormattedMessage id="permissionManager.assignees.successMessage" />,
      );
    } catch (error) {
      const err = error as FetchBaseQueryError;
      if (err.status === 403) return;
      toastService.error(error.data.errorDesc);
    }
  };

  const showMaxOverflowToast = () => {
    const posNames = positionsWithMoreUsersThanMax
      .map(({ positionLong }) => positionLong)
      .toString()
      .replaceAll(',', ', ')
      .trim()
      .slice(0, -1);

    toastService.error(
      <FormattedMessage
        id="permissionManager.assignees.maxOverflowError"
        values={{ positions: posNames }}
      />,
    );
  };

  const handleChange = (value: string) => {
    if (positionsWithMoreUsersThanMax.length) {
      showMaxOverflowToast();
      return;
    }

    // Update position
    if (value && activeList.length && max === 1) {
      isFunctional
        ? handleReasignFunctionalPosition(value, activeList[0])
        : handleReasignRegisteredPosition(value, activeList[0].personGuid);
      return;
    }

    // Add position
    if (value) {
      isFunctional
        ? handleAddFunctionalPosition(value)
        : handleReasignRegisteredPosition(value);
    }
  };

  const handleRemoveFromList =
    (currentPersonAssigned: PersonAssigned) => () => {
      if (
        positionsWithMoreUsersThanMax.length &&
        !positionsWithMoreUsersThanMax.find(
          pos => pos.PositionId === PositionId,
        )
      ) {
        showMaxOverflowToast();
        return;
      }

      isFunctional
        ? handleExpireFunctionalPosition(currentPersonAssigned)
        : handleReasignRegisteredPosition(
            undefined,
            currentPersonAssigned.personGuid,
          );
    };

  return (
    <Card loading={loading} className={styles.positionContainer}>
      <S size="4" colored semibold>
        <div className={styles.selectorTitleContainer}>
          <span>{positionLong}</span>
          <FormattedMessage
            id="permissionManager.assignees.personSelect.minMax"
            values={{ min, max: hasNoMax ? 'no max' : `${max} max` }}
          />
        </div>
      </S>
      <Select
        size="large"
        rounded
        optionFilterProp="children"
        showSearch
        placeholder="Select a Member"
        disabled={
          isSaving || (activeList.length >= max && !(max === 1 && min === 1))
        }
        onChange={handleChange}
        value={undefined}
      >
        {unitUsers.map(user => {
          const { personFullName, personGuid, hasValidRegistration } = user;
          return (
            <Select.Option
              key={personGuid}
              value={personGuid}
              className={cn({
                [styles.lapsedSelected]: !hasValidRegistration,
              })}
              disabled={!hasValidRegistration || !getCanAssignPosition(user)}
            >
              {personFullName}
            </Select.Option>
          );
        })}
      </Select>
      <div className={styles.memberdAddedSection}>
        <S size="6">
          <FormattedMessage id="permissionManager.assignees.personSelect.membersAdded" />
        </S>
        <S size="6">{`${activeList.length} of ${hasNoMax ? '99+' : max}`}</S>
      </div>
      <div className={styles.personListContainer}>
        {activeList.length ? (
          activeList.map(person => (
            <div key={person.pkpersonPositionId} className={styles.listItem}>
              <PersonAvatar
                className={styles.personIcon}
                bordered={false}
                isAdult={!isYouthPosition}
              />
              <S size="5" className={cn(styles.personName)}>
                {person.fullName}
              </S>
              {
                /*(max > 1 || activeList.length > max || min === 0)*/ true && (
                  <div className={styles.removeBtnContainer}>
                    <Button
                      shape="circle"
                      type="ghost"
                      noBorder
                      size="small"
                      onClick={handleRemoveFromList(person)}
                    >
                      <MdOutlineRemoveCircle />
                    </Button>
                  </div>
                )
              }
            </div>
          ))
        ) : (
          <div
            className={cn(styles.emptyMsgContainer, {
              [styles.withLapsedList]: lapsedList.length,
            })}
          >
            {showRequiredMsg && (
              <div className={styles.multipleErrorContainer}>
                <MdError />
                <S size="6">
                  <FormattedMessage id="permissionManager.assignees.personSelect.requiredError" />
                </S>
              </div>
            )}
            <MdPersonAdd />
            <S size="5" semibold>
              <FormattedMessage id="permissionManager.assignees.personSelect.noMembersAdded" />
            </S>
          </div>
        )}
        {!!lapsedList.length && (
          <div>
            <S size="6" className={styles.notCountedTitle}>
              <FormattedMessage id="permissionManager.assignees.personSelect.notCounted" />
            </S>
            <hr className={styles.listDivider} />
            {lapsedList.map(person => (
              <div key={person.pkpersonPositionId} className={styles.listItem}>
                <PersonAvatar
                  className={styles.personIcon}
                  bordered={false}
                  isAdult={!isYouthPosition}
                />
                <S
                  size="5"
                  className={cn(styles.personName, styles.lapsedListItem)}
                >
                  {`${person.fullName} (Term Expires: ${DateTime.fromISO(
                    person.registrationExpireDt,
                  ).toFormat('MM/dd')} Status: ${person.renewalStatus})`}
                </S>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
